"use client"

import * as React from "react"
import { useSearchParams } from "react-router-dom"

import { cn } from "@/lib/utils"
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Search, SlidersHorizontal, X } from "lucide-react"

// Re-export FilterConfig type from shared-types for backward compatibility
export type { FilterConfig } from "@/lib/shared-types/filters"
import type { FilterConfig } from "@/lib/shared-types/filters"

interface FilterBarProps {
  filters: FilterConfig[]
  activeFilters: Record<string, string | string[]>
   
  onFilterChange: (...args: [Record<string, string | string[]>]) => void
  onClearAll?: () => void
  showFiltersLabel?: boolean
  className?: string
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  activeFilters,
  onFilterChange,
  onClearAll,
  showFiltersLabel = true,
  className,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false)
  const searchDebounceRefs = React.useRef<Record<string, number | undefined>>({})

  const searchFilters = React.useMemo(
    () => filters.filter((filter) => filter.type === "search"),
    [filters]
  )
  const drawerFilters = React.useMemo(
    () => filters.filter((filter) => filter.type !== "search"),
    [filters]
  )
  const hasDrawerFilters = drawerFilters.length > 0

  // Handle filter changes
  const handleFilterChange = React.useCallback(
    (filterId: string, value: string | string[]) => {
      const newFilters = { ...activeFilters, [filterId]: value }

      // Update URL search params
      const newParams = new URLSearchParams(searchParams.toString())
      if (typeof value === "string") {
        if (value) {
          newParams.set(filterId, value)
        } else {
          newParams.delete(filterId)
        }
      } else {
        const cleaned = value.filter(Boolean)
        if (cleaned.length > 0) {
          newParams.set(filterId, cleaned.join(","))
        } else {
          newParams.delete(filterId)
        }
      }

      setSearchParams(newParams, { replace: true })
      onFilterChange(newFilters)
    },
    [activeFilters, onFilterChange, searchParams, setSearchParams]
  )

  // Handle search input
  const handleSearchChange = React.useCallback(
    (filterId: string, event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const existingTimer = searchDebounceRefs.current[filterId]
      if (existingTimer) {
        window.clearTimeout(existingTimer)
      }
      searchDebounceRefs.current[filterId] = window.setTimeout(() => {
        const newParams = new URLSearchParams(searchParams.toString())
        if (value) {
          newParams.set(filterId, value)
        } else {
          newParams.delete(filterId)
        }
        setSearchParams(newParams, { replace: true })
        onFilterChange({ ...activeFilters, [filterId]: value })
      }, 300)
    },
    [activeFilters, onFilterChange, searchParams, setSearchParams]
  )

  React.useEffect(() => {
    return () => {
      Object.values(searchDebounceRefs.current).forEach((timer) => {
        if (timer) {
          window.clearTimeout(timer)
        }
      })
    }
  }, [])

  // Clear all filters
  const handleClearAll = React.useCallback(() => {
    Object.values(searchDebounceRefs.current).forEach((timer) => {
      if (timer) {
        window.clearTimeout(timer)
      }
    })
    searchDebounceRefs.current = {}
    const newParams = new URLSearchParams()
    setSearchParams(newParams, { replace: true })
    onFilterChange({})
    setIsFiltersOpen(false)
    if (onClearAll) {
      onClearAll()
    }
  }, [onFilterChange, onClearAll, setSearchParams])

  // Get active filter count — only count keys that correspond to a defined FilterConfig
  // and treat 'all'/''/undefined as inactive
  const activeFilterCount = React.useMemo(() => {
    const filterIds = new Set(filters.map((f) => f.id));
    return Object.keys(activeFilters).filter((key) => {
      if (!filterIds.has(key)) return false;
      const value = activeFilters[key];
      if (Array.isArray(value)) return value.length > 0;
      const str = String(value ?? '');
      return str !== '' && str !== 'all';
    }).length;
  }, [activeFilters, filters])

  // Get filter component based on type
  const getFilterComponent = React.useCallback(
    (filter: FilterConfig) => {
      const currentValue = activeFilters[filter.id] || filter.defaultValue || ""

      switch (filter.type) {
        case "select":
          return (
            <Select
              value={currentValue as string}
              onValueChange={(value) => handleFilterChange(filter.id, value)}
            >
              <SelectTrigger className="w-full">{filter.placeholder || filter.label}</SelectTrigger>
              <SelectContent>
                {filter.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )

        case "multiselect":
          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left">
                  <span className="truncate">{filter.placeholder || filter.label}</span>
                  <X className="ml-2 h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 rounded-xl border-border/70 p-4">
                <div className="space-y-2">
                  {filter.options?.map((option) => (
                    <label key={option.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={Array.isArray(currentValue) && currentValue.includes(option.value)}
                        onCheckedChange={(checked) => {
                          const currentValues = Array.isArray(currentValue) ? currentValue : []
                          const newValues = checked
                            ? [...currentValues, option.value]
                            : currentValues.filter((v) => v !== option.value)
                          handleFilterChange(filter.id, newValues)
                        }}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )

        case "date-range":
          return (
            <div className="grid grid-cols-1 gap-2 @[620px]/filter-bar:grid-cols-2">
              <Input
                type="date"
                value={Array.isArray(currentValue) ? currentValue[0] || "" : ""}
                onChange={(e) => {
                  const startDate = e.target.value
                  const endDate = Array.isArray(currentValue) ? currentValue[1] || "" : ""
                  handleFilterChange(filter.id, [startDate, endDate])
                }}
                placeholder="Start date"
                className="w-full"
              />
              <Input
                type="date"
                value={Array.isArray(currentValue) ? currentValue[1] || "" : ""}
                onChange={(e) => {
                  const endDate = e.target.value
                  const startDate = Array.isArray(currentValue) ? currentValue[0] || "" : ""
                  handleFilterChange(filter.id, [startDate, endDate])
                }}
                placeholder="End date"
                className="w-full"
              />
            </div>
          )

        case "checkbox":
          return (
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={Boolean(currentValue)}
                onCheckedChange={(checked) => handleFilterChange(filter.id, checked ? "true" : "")}
              />
              <span>{filter.label}</span>
            </label>
          )

        default:
          return null
      }
    },
    [activeFilters, handleFilterChange, handleSearchChange]
  )

  const renderSearchFilters = React.useCallback(
    () =>
      searchFilters.length > 0 ? (
        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
          {searchFilters.map((filter) => {
            const currentValue = activeFilters[filter.id] || filter.defaultValue || ""

            return (
              <div key={filter.id} className="relative min-w-[220px] flex-1">
                <label htmlFor={filter.id} className="sr-only">
                  {filter.label}
                </label>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id={filter.id}
                  type="text"
                  value={currentValue as string}
                  onChange={(event) => handleSearchChange(filter.id, event)}
                  placeholder={filter.placeholder || filter.label}
                  className="h-9 pl-9"
                />
              </div>
            )
          })}
        </div>
      ) : null,
    [activeFilters, handleSearchChange, searchFilters]
  )

  const renderDrawerFilters = React.useCallback(
    (isDrawer: boolean) => (
      <div
        className={cn(
          isDrawer
            ? "space-y-3"
            : "grid grid-cols-1 gap-2 @[740px]/filter-bar:grid-cols-2 @[1080px]/filter-bar:grid-cols-3 @[1380px]/filter-bar:grid-cols-4"
        )}
      >
        {drawerFilters.map((filter) => (
          <div
            key={filter.id}
            className={cn(isDrawer && "rounded-xl border border-border/70 bg-muted/30 p-3")}
          >
            {isDrawer && <div className="mb-2 text-sm font-medium">{filter.label}</div>}
            {getFilterComponent(filter)}
          </div>
        ))}
      </div>
    ),
    [drawerFilters, getFilterComponent]
  )

  return (
    <div className={cn("@container/filter-bar", className)}>
      <div className="flex flex-col gap-2 @[740px]/filter-bar:flex-row @[740px]/filter-bar:items-start @[740px]/filter-bar:justify-between">
        {renderSearchFilters()}

        {(hasDrawerFilters || activeFilterCount > 0) && (
          <div className="flex items-center gap-2">
            {hasDrawerFilters && (
              <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className={cn("h-9", !showFiltersLabel && "w-9 px-0")}>
                    <SlidersHorizontal className={cn("h-4 w-4", showFiltersLabel && "mr-2")} />
                    {showFiltersLabel ? "Filters" : <span className="sr-only">Filters</span>}
                    {activeFilterCount > 0 && (
                      <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[min(92vw,440px)] p-0">
                  <SheetHeader className="border-b p-4 pr-12">
                    <SheetTitle>Filters</SheetTitle>
                    <SheetDescription>
                      Refine the list with filter options.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-4">
                    {renderDrawerFilters(true)}
                  </div>
                  <div className="border-t p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-muted-foreground">
                        {activeFilterCount} active filter{activeFilterCount === 1 ? "" : "s"}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClearAll}
                        disabled={activeFilterCount === 0}
                      >
                        Clear all
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}

            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClearAll} className="h-9 px-2">
                Clear
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FilterBar
